import { useEffect, useReducer } from 'react';
import { Box, Button, Link, Paper, Stack, Typography } from '@mui/material';
import { Trans, useTranslation } from 'react-i18next';
import {
  denyConsent,
  grantConsent,
  needsConsentBanner,
  subscribeTrackingConfig,
  trackPageView,
} from '@/analytics/ga';
import { useEmbed } from '@/context/EmbedContext';

const PRIVACY_POLICY_URL = 'https://sala.gr/privacy-policy';

/**
 * Cookie consent banner for platform GA/GTM and per-store Meta / GA4 / Google Ads.
 * Shows when any tracker is configured and the user has not chosen yet.
 */
export function CookieConsent() {
  const { t } = useTranslation();
  const { isEmbedded } = useEmbed();
  const [, rerender] = useReducer((n: number) => n + 1, 0);

  useEffect(() => subscribeTrackingConfig(rerender), []);

  if (!needsConsentBanner()) {
    return null;
  }

  const handleAccept = () => {
    grantConsent();
    trackPageView(window.location.pathname + window.location.search);
  };

  const handleReject = () => {
    denyConsent();
  };

  return (
    <Paper
      elevation={8}
      role="dialog"
      aria-label={t('cookieConsent.ariaLabel')}
      sx={{
        position: 'fixed',
        left: isEmbedded ? 8 : 16,
        right: isEmbedded ? 8 : 16,
        bottom: isEmbedded ? 8 : 16,
        zIndex: (theme) => theme.zIndex.snackbar,
        p: isEmbedded ? 1.5 : 2,
        maxWidth: 720,
        mx: 'auto',
        backgroundColor: 'background.paper',
      }}
    >
      <Stack spacing={isEmbedded ? 1.5 : 2}>
        <Box>
          <Typography variant={isEmbedded ? 'subtitle2' : 'subtitle1'} fontWeight={600} gutterBottom>
            {t('cookieConsent.title')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            <Trans
              i18nKey="cookieConsent.message"
              components={{
                privacyLink: (
                  <Link
                    href={PRIVACY_POLICY_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    underline="hover"
                  />
                ),
              }}
            />
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} justifyContent="flex-end" flexWrap="wrap" useFlexGap>
          <Button
            variant="outlined"
            color="inherit"
            size={isEmbedded ? 'small' : 'medium'}
            onClick={handleReject}
          >
            {t('cookieConsent.reject')}
          </Button>
          <Button
            variant="contained"
            color="primary"
            size={isEmbedded ? 'small' : 'medium'}
            onClick={handleAccept}
          >
            {t('cookieConsent.accept')}
          </Button>
        </Stack>
      </Stack>
    </Paper>
  );
}
